/* eslint-disable import/no-default-export */
import classNames from 'classnames';
import { forNamespace } from 'app/common/lib/util/i18n';
import pollify from 'app/common/lib/util/pollify';
import Announcements, {
  POPOVER_NAME,
} from 'app/gamma/src/components/popovers/announcements';
import {
  loadAllAnnouncements,
  loadAnnouncements,
  showPopover,
  State,
} from 'app/gamma/src/modules';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { isPopoverShown } from 'app/gamma/src/selectors/popovers';
import {
  getUnreadAnnouncementCount,
  includesDowntime,
  isLoading,
  isRecentlyRead,
} from 'app/gamma/src/selectors/announcements';
import styles from './header.less';

import { Noun, Verb, trackUe } from '@trello/analytics';

const format = forNamespace('announcements');

const ONE_SECOND = 1000;
const THIRTY_SECONDS = ONE_SECOND * 30;
const ONE_MINUTE = THIRTY_SECONDS * 2;
const TEN_MINUTES = ONE_MINUTE * 10;

interface AnnouncementsProps {
  loading: boolean;
  interestingAnnouncementCount: number;
  downtime: boolean;
  showAnnouncements: boolean;
}

interface StateProps {
  showTaco: boolean;
}

interface DispatchProps {
  readonly updateAnnouncements: () => void;
  readonly updateAllAnnouncements: () => void;
  readonly openRooAnnouncements: () => void;
}

interface AllProps extends AnnouncementsProps, DispatchProps {}

class Roo extends React.Component<AllProps, StateProps> {
  rooButtonRef = React.createRef<HTMLButtonElement>();
  interval: number;
  state = { showTaco: isRecentlyRead() };

  private openAnnouncementsPopover = () => {
    this.props.updateAllAnnouncements();
    this.props.openRooAnnouncements();
    this.setState({
      showTaco: true,
    });

    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.ANNOUNCEMENTS,
      context: {
        interestingAnnouncementCount: this.props.interestingAnnouncementCount,
      },
    });
  };

  private shouldTacoShow = () => {
    this.setState({
      showTaco: isRecentlyRead(),
    });
  };

  private pollingShouldTacoShow = pollify(this.shouldTacoShow, THIRTY_SECONDS);
  private pollingUpdateAnnouncements = pollify(
    this.props.updateAnnouncements,
    TEN_MINUTES,
  );

  componentDidMount() {
    this.pollingUpdateAnnouncements();
    this.pollingShouldTacoShow();
  }

  componentWillUnmount() {
    this.pollingUpdateAnnouncements.clear();
    this.pollingShouldTacoShow.clear();
  }

  render() {
    const {
      interestingAnnouncementCount,
      downtime,
      showAnnouncements,
    } = this.props;

    const { showTaco } = this.state;
    const tacoClasses = {
      [styles.rooUp]: false,
      [styles.rooCrouch]: false,
      [styles.rooAlert]: false,
    };

    let rooText = '';

    if (downtime) {
      tacoClasses[styles.rooAlert] = true;
      rooText = format('maintenance');
    } else {
      rooText = format('new stuff');
    }

    if (interestingAnnouncementCount > 0) {
      tacoClasses[styles.rooUp] = true;
    } else if (showTaco) {
      tacoClasses[styles.rooCrouch] = true;
    } else {
      // Render nothing if there are no announcements and we don't need to show Taco.
      return null;
    }

    return (
      <div className={styles.rooContainer}>
        <button
          className={classNames({
            [styles.rooButton]: true,
            ...tacoClasses,
          })}
          ref={this.rooButtonRef}
          onClick={this.openAnnouncementsPopover}
        >
          <span className={styles.tacoRooIcon} />
          <span className={styles.rooText}>
            <span className={styles.leftArrow} />
            <span>{rooText}</span>
          </span>
          {showAnnouncements && (
            <Announcements target={() => this.rooButtonRef.current} />
          )}
        </button>
      </div>
    );
  }
}

function mapStateToProps(state: State) {
  return {
    loading: isLoading(state),
    interestingAnnouncementCount: getUnreadAnnouncementCount(state),
    downtime: includesDowntime(state),
    showAnnouncements: isPopoverShown(state, POPOVER_NAME),
  };
}

function mapDispatchToProps(dispatch: Dispatch<State>) {
  return {
    updateAnnouncements() {
      dispatch(loadAnnouncements());
    },
    updateAllAnnouncements() {
      dispatch(loadAllAnnouncements());
    },
    openRooAnnouncements() {
      trackUe({
        category: Noun.HEADER,
        verb: Verb.OPENS,
        directObj: Noun.ANNOUNCEMENTS,
        context: {
          type: 'Roo',
        },
      });

      dispatch(
        showPopover({
          name: POPOVER_NAME,
        }),
      );
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Roo);
